/**
 * Sometimes, figma-exported .png file is imported as BLOB rather than a resolved image path.
 * In that case, use export image as an svg.
 */
import { CAPITALIZATION_VALUES, DOMAIN_TYPES } from "../constants";
import commentsWidgetImage from "./img/comments_widget.jpg";
import checkGreenRound from "./img/check-green-round.png";
import cards from "./img/cards.png";
import cvvIcon from "./img/cvv-icon.png";
import guarantee from "./img/guarantee.png";
import greenLock from "./img/green-lock.svg";
import quizImage from "./img/multi-step-quiz.png";
import quizImageV3 from "./img/multi-step-quiz-v3.svg";
import quizImageV4 from "./img/multi-step-quiz-v4.svg";
import ageQuizImage from "./img/age-quiz.png";
import liveClaimFeedImage from "./img/live-claim-feed.png";
import spinWheelImage from "./img/spin-wheel.png";
import imageBannerImage from "./img/image-banner.png";
import socialProofImage from "./img/social-proof.png";
import socialProofV2Image from "./img/social-proof-v2.png";
import newsletterBannerImage from "./img/newsletter-banner.jpg";
import numberedStepsImage from "./img/numbered-steps.png";

import toastImage from "./img/toast-img.png";
import livechatImage from "./img/live-chat-img.svg";
import livechatV2Image from "./img/live-chat-v2-img.svg";
import liveChatMp4Image from "./img/live-chat-mp4.png";
import liveChatMp4V2Image from "./img/live-chat-mp4-v2-img.svg";
import audioFormImage from "./img/audio-form-img.png";
import fourStars from "./img/stars_4_and_half.svg";
import editor from "./img/avatars/editor.svg";
import developer from "./img/avatars/developer.svg";
import association_member from "./img/avatars/association_member.svg";
import analyst from "./img/avatars/analyst.svg";
import bottomBarImage from "./img/bottom-bar.png";
import zipCodeImage from "./img/zip-code-img.svg";
import multiStepFormImage from "./img/multi-step-quiz.png";

import ringbaPhoneNumberButtonImage from "./img/ringba-phone-number-button.svg";
import ringbaPhoneNumberLinkAndButtonImage from "./img/ringba-phone-number-link-and-button.svg";

import { hexToRgb, rgbToHex } from "@/@core/utils";
import { calculateTimeRemaining } from "@/utility/calculateTimeRemaining";
import { encodeLiveChatCTAV2Data } from "@/assets/utilities/tinymce/helpers/liveChatCTAV2";
import { CONTENT_BOX_BORDER_SIDES } from "@/constant";

const IMAGES_API = process.env.VUE_APP_API;

const getGifAgeTemplate = options => {
  return `
    <div>
      <a
        href="${options.url}"
        target="${options.tab}"
      >
        <img class="mx-auto" src="${IMAGES_API}/images/misc/age-gif.gif" alt=""
        data-image>
      </a>
    </div>
  `;
};

const getCTATemplateWidthAd = options => {
  return `
    <a
      data-shortcode='buttons'
      class="button-width-ad flex justify-center align-center w-full bg-gradient-to-b from-green-6 to-green-7 text-22 leading-26 font-bold text-white px-16 py-6 md:py-18px rounded-xl border-b-5 border-green-8 relative"
      href="${options.url}"
      target="${options.tab}"
      >
        <span class="text-center">
           ${options.text}
           <img
            class="ml-4 inline"
            src="${IMAGES_API}/images/misc/arrow.svg"
            data-image
            alt="Arrow"
          >
        </span>

        <span class="absolute text-13 bottom-0 right-2 leading-none">
            Ad
        </span>
    </a>
  `;
};

const getCTATemplate = options => {
  return `
    <a
      style="background: ${options?.bgColor};"
      data-shortcode='buttons'
      class="button button--green"
      href="${options?.url}"
      target="${options?.tab}"
      >
      <div class="">
        <span>${options?.text}</span>
        <img
          class="inline ml-2"
          src="${IMAGES_API}/images/misc/arrow.svg"
          data-image
          alt="Arrow"
        >
      </div>
    </a>
  `;
};

const getSelectAgeTemplate = options => {
  const ages = ["Under 30", "30-39", "40-49", "50-59", "60-69", "Over 70"];
  let template = "<div class='mx-auto max-w-3.5xl'><div class='select-age__button-container'>";

  for (const age of ages) {
    template += `
      <a
        data-shortcode="buttons"
        href="${options?.url}"
        target="${options?.tab}"
        class="select-age__button--green ${options?.type}-bg"
      >
        ${age}
      </a>
    `;
  }
  template += "</div></div>";

  return template;
};

const getSelectAgeIconsTemplate = options => {
  const ages = ["<40", "40 - 49", "50 - 59", "60 - 69", "70+"];
  const genders = ["m", "f"];
  let template = '<div class="max-w-5xl inline-flex flex-wrap justify-center md:justify-normal">';
  genders.forEach(gender => {
    ages.forEach((age, index) => {
      template += `
      <a
      href="${options?.url}"
      target="${options?.tab}"
      class="age-icons__item ${options?.color}-bg"
      >
      <img
          src='${IMAGES_API}/images/peoples/age-avatars/${gender}-${index + 1}.svg')
          data-image alt="avatar image" />
          ${age}
        </a>
      `;
    });
  });
  template += "</div>";

  return template;
};

const getMileageTemplate = options => {
  const miles = ["30 or less", "31 - 59 miles", "60+ miles"];

  let template = '<div data-shortcode="buttons" class="flex -mx-3">';

  for (const mile of miles) {
    template += `
    <div class="px-3 w-full">
      <div class="button w-full font-bold flex h-full items-center text-22 md:text-24 justify-center leading-snug">
        <a
          href="${options?.url}"
          target="${options?.tab}"
          class="button w-full font-bold flex h-full items-center text-22 md:text-24 justify-center button--green ${options?.color}-bg"
        >
          ${mile}
      </a>
      </div>
    </div>
    `;
  }
  template += "</div>";

  return template;
};

const getCtaButtonNumberTemplate = options => {
  let targetAttribute = "";
  if (!options?.hrefType) {
    targetAttribute = `target="${options?.tab}"`;
  }

  return `
    <a
      data-shortcode='buttons'
      style="background-color: ${options?.bgColor}; border-color: ${options?.borderColor};"
      class="mx-auto flex max-w-45 border-2 rounded-lg shadow-gray-bottom-light px-10 md:px-16 py-5 md:py-6 items-center font-roboto no-underline justify-center button-animation-pulsing text-white"
      href="${options?.hrefType}${options?.url}"
      ${targetAttribute}
    >
        <div class="mr-6 md:mr-35 w-30px md:w-auto">
            <img data-image src="${IMAGES_API}/images/phone.svg" alt="phone icon">
        </div>
        <div>
            <div class="text-white text-22 md:text-36 leading-30 md:leading-40 font-extrabold">${options?.number}</div>
            <div class="text-white text-14 md:text-16 leading-20 md:leading-24 font-semibold">${options?.text}</div>
        </div>
    </a>
  `;
};

const getCTAPhoneTemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button button--green"
      href="tel:${options?.number}"
      >
      <div class="flex justify-center">
        <span>${options?.text}</span>
        <img
          class="inline ml-2 w-22px"
          src="${IMAGES_API}/images/phone.svg"
          data-image
          alt="phone icon"
        >
      </div>
    </a>
  `;
};

const getCTAIndyTemplate = options => {
  return `
       <a
      data-shortcode='buttons'
      class="button-indy button-indy--${options.type}"
      href="${options.url}"
      target="${options.tab}"
      >
           <span>${options.text}</span>
           <img
               class="ml-4 md:ml-8"
               src="${IMAGES_API}/images/indy_arrow.svg"
               data-image
               alt="Arrow"
           >
    </a>
  `;
};

const getShortcodeHTMLTemplate = content => {
  const template = `
    <div>
      <div class="advert--title">ADVERT</div>
      <div class="custom-partner" data-tiny-mce-component="custom-partner">
        ${content?.html}
      </div>
    </div>
    `;

  return template;
};

const getShortCTATemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button button--short button--short--green"
      href="${options?.url}"
      target="${options?.tab}"
      >
           <span>${options?.text}</span>
    </a>
    `;
};

const getShortCTA4Template = options => {
  const bgColor = options?.bgColor || "#28b02b";
  const textColor = options?.textColor || "#ffffff";
  return `
      <a
        data-shortcode='buttons'
        class="button-cta-4"
        style="background: ${bgColor}; border: 1px solid ${bgColor}; color: ${textColor};"
        href="${options?.url || "#"}"
        target="${options?.tab || "_self"}"
      >
        ${options?.text || ""}
      </a>
    `;
};

const getCustomButtonTemplate = options => {
  return `
    <a
      data-shortcode="buttons"
      class="${options?.classNames}"
      style="${options?.style}"
      href="${options?.href}"
      target="${options?.target}"
      data-editable="false"
    >
      ${options?.iconMarkup}
      <span class="custom-button__content">
        <span class="custom-button__text">${options?.text}</span>
        ${
          options?.secondaryText
            ? `<span class="custom-button__secondary" style="color: ${options.secondaryTextColor};">${options.secondaryText}</span>`
            : ""
        }
      </span>
      ${options?.enableAd ? `<span class="custom-button__ad">Ad</span>` : ""}
    </a>
  `;
};

const getLiveChatCTATemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button--livechat ${options?.pulsingAnimation ? "animated-button" : ""} ${
    options?.spaceBellow ? "mb-16px" : ""
  }"
      href="${options?.url}"
      >
           <span>${options?.text}</span>
    </a>
    `;
};

const normalizeLiveChatCTAV2Options = options => ({
  twoLine: !!options?.twoLine,
  line1: options?.line1 ?? "",
  line2: options?.line2 ?? "",
  bgColor: options?.bgColor || "#28b02b",
  textColor: options?.textColor || "#ffffff",
  fontFamily: options?.fontFamily || "",
  fontSize: options?.fontSize || "",
  fontSize2: options?.fontSize2 || options?.fontSize || "",
  iconSrc: (options?.iconSrc ?? "").trim(),
  url: options?.url ?? "",
  pulsingAnimation: !!options?.pulsingAnimation,
  spaceBellow: !!options?.spaceBellow,
});

const getLiveChatCTAV2Template = options => {
  const normalized = normalizeLiveChatCTAV2Options(options);
  const {
    twoLine,
    line1,
    line2,
    bgColor,
    textColor,
    fontFamily,
    fontSize,
    fontSize2,
    iconSrc,
    url,
    pulsingAnimation,
    spaceBellow,
  } = normalized;
  const PREVIEW_FONT_SIZE_WIDTH_BASIS_PX = 560;
  const cappedFontSize1 = Math.min(
    parseFloat(fontSize) || 18,
    PREVIEW_FONT_SIZE_WIDTH_BASIS_PX / Math.max(line1.length, 1),
  );
  const cappedFontSize2 = Math.min(
    parseFloat(fontSize2) || 18,
    PREVIEW_FONT_SIZE_WIDTH_BASIS_PX / Math.max(line2.length, 1),
  );

  const icon = iconSrc ? `<img class="button--livechat-cta-v2__icon" src="${iconSrc}" data-image alt="icon">` : "";
  const lines = twoLine
    ? `<span style="font-size: ${cappedFontSize1}px;">${line1}</span><span style="font-size: ${cappedFontSize2}px;">${line2}</span>`
    : `<span style="font-size: ${cappedFontSize1}px;">${line1}</span>`;

  const classes = [
    "button--livechat-cta-v2",
    twoLine && "button--livechat-cta-v2--two-line",
    pulsingAnimation && "animated-button",
    spaceBellow && "mb-16px",
  ]
    .filter(Boolean)
    .join(" ");

  const rootStyle = `background: ${bgColor}; color: ${textColor};${fontFamily ? ` font-family: ${fontFamily};` : ""}`;

  return `
    <a
      data-shortcode='buttons'
      live-chat-cta-v2-data="${encodeLiveChatCTAV2Data(normalized)}"
      style="${rootStyle}"
      class="${classes}"
      href="${url}"
      >
      ${icon}
      <span class="button--livechat-cta-v2__text">${lines}</span>
    </a>
    `;
};

const getVueLiveChatCTAV2Template = (options = {}) => {
  const encoded = encodeLiveChatCTAV2Data(normalizeLiveChatCTAV2Options(options));

  return `<live-chat-cta-v2 live-chat-cta-v2-data='${encoded}'></live-chat-cta-v2>`;
};

const getHeadlineWithBgTemplate = options => {
  return `
    ${
      options?.enableBorders
        ? `<hr
            class="headline-hr"
            style="border-color: ${options?.bordersColor}"
          >`
        : ""
    }

    <div
      class="headline"
      style="${options?.enableBackgroundColor && options?.bgColor ? `background: ${options.bgColor}` : ""}"
    >
      <h1 class="headline-text">
         ${options?.text}
      </h1>
    </div>

     ${
       options?.enableBorders
         ? `<hr
            class="headline-hr"
            style="border-color: ${options?.bordersColor}"
          >`
         : ""
     }
    `;
};

const getCTAOuterTemplate = options => {
  return `
       <div class="md:flex" >
           <div class="md:mr-12">
               <a
               class="button-outer"
               href="${options.url}"
               target="${options.tab}"
               >
                   <span>${options.text}</span>
                   <img
                       class="ml-4 md:ml-8"
                       src="${IMAGES_API}/images/indy_arrow.svg"
                       data-image
                       alt="Arrow"
                   >
               </a>
           </div>
           <div class="hidden md:flex items-center text-18 font-semibold leading-30 ">
                Press Button
                <img src="${IMAGES_API}/images/arrow-left.svg" data-image class="ml-4" alt="arrow">
           </div>
       </div>
  `;
};

const getMultipleCTATemplate = options => {
  if (options?.form === "squared") {
    return `
  <div>
    <div class="mt-4 mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="button button--yes-no button--green"
        href="${options?.url}"
        target="${options?.tab}"
        >
            <span>YES</span>
      </a>
    </div>
    <div class="mb-4 mt-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="button button--yes-no button--green"
        href="${options?.url}"
        target="${options?.tab}"
      >
          <span>NO</span>
      </a>
    </div>
  </div>
  `;
  } else {
    return `
  <div>
    <div class="mt-4 mb-6 md:mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="buttons-rounded buttons-rounded--green"
        href="${options?.url}"
        target="${options?.tab}"
        >
            <span>YES</span>
      </a>
    </div>
    <div class="mt-4 mb-6 md:mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="buttons-rounded buttons-rounded--green"
        href="${options?.url}"
        target="${options?.tab}"
      >
          <span>NO</span>
      </a>
    </div>
  </div>
  `;
  }
};

const getCheckoutPlanTemplate = () => {
  return `
    <div data-shortcode='checkoutplan' class="checkout-plan">
      <div class="border-4 border-green-5 rounded-xl px-6 py-8 flex items-baseline">
        <div>
            <div class="w-22px">
                <img src="${checkGreenRound}" alt="">
              </div>
            </div>
            <div class="ml-8">
                <div class="text-26 leading-38 md:text-33 md:leading-46 text-black font-bold font-oswald">
                Lifetime Access To Dollar Save - $39
                 </div>
                 <div class="bg-yellow-1 text-16 leading-28 font-medium px-4 font-oswald mt-6 md:w-max pb-1">
                                BEST DEAL: Save 80% -- One-time payment of $39
                </div>
                <div class="mt-6 ml-8">
                    <ul class="font-semibold text-black list-disc checkout-plan__list font-open">
                        <li>
                            Money for a Lifetime: 7 Simple Steps to Getting the Most from Social Security ($49 Value) - FREE
                         </li>
                       </ul>
                 </div>
               </div>
           </div>
    </div>
  `;
};

const getQuoteBoxTemplate = () => {
  return `
    <div data-shortcode='quoteratings' class="quote-rating">
      <div class="quote-rating__wrapper">
        <div class="quote-rating__quote">
          Quote
        </div>
        <div class="quote-rating__author">
          Author
          <div>
            <img src="${IMAGES_API}/images/icons/stars_5.svg" alt="Star icons">
            </div>
          </div>
        </div>
      </div>
  `;
};

const getQuoteTemplate = () => {
  return `
    <div data-shortcode='quote' class="quote">
      Quote
    </div>
  `;
};

const getWhiteMultiButtonCta = options => {
  const defaultTextItems = {
    "#E01107": "Unsold Luxury SUV Pricing",
    "#2B9F11": "Clearance Pricing On Unsold SUVs",
    "#F87012": "Unsold Luxury SUV Prices (You May Be Surprised)",
    "#0561D8": "Midsize Crossover SUVs Prices Now",
  };
  if (options?.quantity === "5") {
    defaultTextItems["#A7BB3C"] = "Free Bank Accounts That Offer Large Cash Bonuses";
  }
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';

  for (const [key, value] of Object.entries(defaultTextItems)) {
    template += `
      <div class="multi-button-cta-white block bg-gray-12 rounded-15px shadow-f-solid text-18 font-bold leading-24 border-2 border-white flex justify-between items-center min-h-60px">
        <a
          data-shortcode='buttons'
          href="${options?.url}"
          target="${options?.tab}"
          class="w-full py-4px pl-8 pr-69px"
        >
          <span>${value}</span>
        </a>
        <svg width="48" height="48" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29.9102 0.341309C45.9303 0.341309 58.9102 13.3211 58.9102 29.3413C58.9102 45.3615 45.9303 58.3413 29.9102 58.3413C13.89 58.3413 0.910156 45.3615 0.910156 29.3413C0.910156 13.3211 13.89 0.341309 29.9102 0.341309ZM43.2291 27.3534L27.3844 11.5086C26.2852 10.4095 24.5077 10.4095 23.4202 11.5086L21.4323 13.4966C20.3331 14.5957 20.3331 16.3732 21.4323 17.4607L33.313 29.3413L21.4323 41.222C20.3331 42.3212 20.3331 44.0986 21.4323 45.1861L23.4202 47.174C24.5194 48.2732 26.2969 48.2732 27.3844 47.174L43.2291 31.3292C44.3283 30.23 44.3283 28.4526 43.2291 27.3534Z" fill="${key}"/>
        </svg>
     </div>
    `;
  }

  template += "</div>";

  return template;
};

const getMultiButtonCta = options => {
  const defaultTextItmes = [
    "Unsold Luxury SUV Senior Pricing",
    "Unsold  SUV Prices (You Be Surprised)",
    "2020 Jeep Grand Cherokee Offers",
    "Midsize Crossover SUVs Prices Now",
  ];
  if (options?.quantity === "5") {
    defaultTextItmes.push("Free Bank Accounts That Offer Large Cash Bonuses");
  }
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';

  for (const item of defaultTextItmes) {
    template += `
      <div class="multi-button-cta-item block bg-gradient-to-b from-blue-401 via-blue-402 to-blue-403 rounded-20px shadow-b-r-heavy text-18 font-bold leading-24 border-2 border-blue-404 flex text-white justify-between min-h-60px">
        <a
          data-shortcode='buttons'
          class="w-full buttons"
          href="${options?.data?.url}"
          target="${options?.data?.tab}"
        >
          <span>${item}</span>
        </a>
        <img class="" src="${IMAGES_API}/images/arrow-right.svg" data-image alt="arrow right">
     </div>
    `;
  }

  template += "</div>";

  return template;
};

const getMultiButtonCtaWithSeparateLinks = options => {
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';
  for (let x = 1; x <= options?.quantity; x++) {
    template += `
      <div class="multi-button-cta-item block bg-gradient-to-b from-blue-401 via-blue-402 to-blue-403 rounded-20px shadow-b-r-heavy text-18 font-bold leading-24 border-2 border-blue-404 flex text-white justify-between min-h-60px">
        <a
          data-shortcode='buttons'
          class="py-4px pl-9 pr-16 w-full buttons flex items-center"
          href="${options?.data["customUrl" + x]}"
          target="${options?.data["openLink" + x]}"
        >
          <span>${options?.data["buttonInput" + x]}</span>
        </a>
        <img class="" src="${IMAGES_API}/images/arrow-right.svg" data-image alt="arrow right">
     </div>
    `;
  }
  template += "</div>";

  return template;
};

const getMultiButtonCtaV3Template = options => {
  let template = '<div class="multi-button-cta-V3">';
  if (options?.count) {
    for (let i = 1; i <= options?.count; i++) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="multi-button-cta-V3__button"
        style="background-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'"
      >
        Text - ${i}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getMultiButtonCtaV4Template = options => {
  const ctaOptions = [
    {
      title: "Healthy Groceries",
      image: `${IMAGES_API}/images/cta-icon-1.svg`,
      alt: "groceries",
    },
    {
      title: "Utilities",
      image: `${IMAGES_API}/images/cta-icon-2.svg`,
      alt: "utilities",
    },
    {
      title: "OTC & Prescription",
      image: `${IMAGES_API}/images/cta-icon-3.svg`,
      alt: "medicine",
    },
    {
      title: "Transportation",
      image: `${IMAGES_API}/images/cta-icon-4.svg`,
      alt: "car",
    },
    {
      title: "Dental",
      image: `${IMAGES_API}/images/cta-icon-5.svg`,
      alt: "tooth",
    },
    {
      title: "Vision & Hearing",
      image: `${IMAGES_API}/images/cta-icon-6.svg`,
      alt: "ear",
    },
  ];

  let template = '<div class="multi-button-cta-V4">';
  for (const cta of ctaOptions) {
    template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="multi-button-cta-V4__button button-animation-pulsing-1025"
        style="background-color: ${options?.bgOptionColor};"
      >
      <span>${cta.title}</span>
      <img src="${cta.image}" alt="${cta.alt}" data-image>
      </a>
    `;
  }

  template += "</div>";
  template += `
    <a
      href="${options?.url}"
      target="${options?.tab}"
      class="multi-button-cta-V4__all-button button-animation-pulsing-1025"
      style="background-color: ${options?.bgMainColor};"
    >
    <span>All of the Above
    <img class="mobile-img" src="${IMAGES_API}/images/checkmark-red.svg" alt="checkmark" data-image></span>
    </a>
  `;

  return template;
};

const getDynamicColorMultiButtonCta = options => {
  const iconsColors = ["#E01107", "#9E12AA", "#DE9258", "#265C9B"];
  if (options?.styles?.quantity === "5") {
    iconsColors.push("#A7BB3C");
  }

  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between max-w-76">';
  let count = 1;

  for (const key of iconsColors) {
    template += `
      <div
        onMouseOver="this.style.backgroundColor='${options?.styles?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.styles?.bgColor}'"
        style="background-color: ${options?.styles?.bgColor}"
        class="multi-button-cta-dynamic block rounded-15px shadow-f-solid text-18 font-bold leading-24 flex justify-between items-center min-h-60px">
        <a
          data-shortcode='buttons'
          href="${options?.data?.["customUrl" + count]}"
          target="${options?.data?.["openLink" + count]}"
          class="w-full py-4px pl-6 pr-69px flex items-center"
        >
          <span class="mr-4 text-22">${count}</span>
          <span>${options?.data?.["buttonInput" + count]}</span>
        </a>
        <svg width="46" height="46" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29.9102 0.341309C45.9303 0.341309 58.9102 13.3211 58.9102 29.3413C58.9102 45.3615 45.9303 58.3413 29.9102 58.3413C13.89 58.3413 0.910156 45.3615 0.910156 29.3413C0.910156 13.3211 13.89 0.341309 29.9102 0.341309ZM43.2291 27.3534L27.3844 11.5086C26.2852 10.4095 24.5077 10.4095 23.4202 11.5086L21.4323 13.4966C20.3331 14.5957 20.3331 16.3732 21.4323 17.4607L33.313 29.3413L21.4323 41.222C20.3331 42.3212 20.3331 44.0986 21.4323 45.1861L23.4202 47.174C24.5194 48.2732 26.2969 48.2732 27.3844 47.174L43.2291 31.3292C44.3283 30.23 44.3283 28.4526 43.2291 27.3534Z" fill="${key}"/>
        </svg>
     </div>
    `;
    count++;
  }

  template += "</div>";

  return template;
};

const getMoneyCTATemplate = () => {
  const moneyOptions = [
    {
      title: "$0 - $15K",
      image: `${IMAGES_API}/images/money-1.svg`,
      alt: "money-1",
    },
    {
      title: "$16K - $25K",
      image: `${IMAGES_API}/images/money-2.svg`,
      alt: "money-2",
    },
    {
      title: "$26K - $50K",
      image: `${IMAGES_API}/images/money-3.svg`,
      alt: "money-3",
    },
    {
      title: "$51K - $100K",
      image: `${IMAGES_API}/images/money-4.svg`,
      alt: "money-4",
    },
    {
      title: "$100K+",
      image: `${IMAGES_API}/images/money-5.svg`,
      alt: "money-5",
    },
  ];

  let template = '<div class="money-cta-buttons-wrapper">';

  for (const money of moneyOptions) {
    template += `
        <a
          class="money-cta-button"
          href="#"
        >
          <img src="${money.image}" alt="${money.alt}" data-image>
          <span>${money.title}</span>
        </a>
    `;
  }

  template += "</div>";

  return template;
};

const getButtonsWithTextBoxTemplate = (data, buttonArray, boxArray) => {
  if (!data) {
    return "buttons-with-text-box";
  }
  let template = '<div class="buttons-with-text-box"><div class="wrapper">';

  data.buttons.forEach((button, index) => {
    if (button.type === "url") {
      template += `
        <a
          href="${button.url}"
          target="${button.tab}"
          class="button-with-text-box-link"
          style="background-color: ${data.primaryColor};"
        >
          ${buttonArray?.[index] ? buttonArray[index] : `<span>${button.text}</span>`}
        </a>
      `;
    } else {
      template += `
        <div
          class="button-with-text-box-link"
          style="background-color: ${data.primaryColor};"
        >
          ${buttonArray?.[index] ? buttonArray[index] : `<span>${button.text}</span>`}
        </div>`;
    }
  });

  template += "</div>";

  data.buttons.forEach((button, index) => {
    if (button.type === "textBox") {
      template += `
        <div class="button-with-text-box-text" data-button-index="${index}" style="border-color: ${
        button.textBorderColor
      }; background-color: ${button.textBgColor}">
          ${boxArray?.[index] ? boxArray[index] : `<span>${button.text}</span>`}
        </div>
      `;
    }
  });

  template += "</div>";

  return template;
};

const getButtonsWithTextBoxVueTemplate = (data, buttonsArray, boxArray) => {
  return `<buttons-with-text-box data="${data}" buttons-array="${buttonsArray}" box-array="${boxArray}"></buttonsWithTextBox>`;
};

const getSelectAgeInOneRowTemplate = options => {
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = `<div class="select-one-row age-cta-2" style="--stack-gap: ${options?.buttonSpacing}px;">`;
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; border-radius: ${options?.cornerRadius}px;"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; border-radius: ${options?.cornerRadius}px;"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};
const getAgeCTA3Template = options => {
  let shadowStyle = "";
  if (options) {
    const backgroundRGB = hexToRgb(options?.bgColor);
    const shadowColorThreshold = 80;
    const shadowRGB = {};
    const shadowR = backgroundRGB.r - shadowColorThreshold;
    const shadowG = backgroundRGB.g - shadowColorThreshold;
    const shadowB = backgroundRGB.b - shadowColorThreshold;

    shadowRGB.r = shadowR < 0 ? 0 : shadowR;
    shadowRGB.g = shadowG < 0 ? 0 : shadowG;
    shadowRGB.b = shadowB < 0 ? 0 : shadowB;
    const shadowHex = rgbToHex(shadowRGB.r, shadowRGB.g, shadowRGB.b);
    shadowStyle = `box-shadow: 0px 4px 4px 0px ${shadowHex};`;
  }
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = '<div class="select-one-row age-cta-3">';
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; ${
        shadowStyle ? shadowStyle : ""
      } border-radius: ${options?.cornerRadius}px;"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; ${
        shadowStyle ? shadowStyle : ""
      } border-radius: ${options?.cornerRadius}px;"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${
        options?.hoverColor
      }'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getAgeCTA4Template = options => {
  if (!options) {
    return "age-cta-4";
  }

  const titles = {
    1: ["Age: <40", "Age: 40-49", "Age: 50-59", "Age: 60-69", "Age: 70+"],
    2: {
      desktop: ["<40", "40-49", "50-59", "60-69", "70+"],
      mobile: ["Age: <40", "Age: 40-49", "Age: 50-59", "Age: 60-69", "Age: 70+"],
    },
    3: ["<40", "40-49", "50-59", "60-69", "70+", "<40", "40-49", "50-59", "60-69", "70+"],
    4: ["<40", "40-49", "50-59", "60-69", "70+", "<40", "40-49", "50-59", "60-69", "70+"],
  };
  const images = [];

  if (options.version == 1 || options.version == 2) {
    for (let i = 1; i <= 5; i++) {
      if (options.version == 2) {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version],
        });
      } else {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
        });
      }
    }
  } else if (options.version == 3 || options.version == 4) {
    for (let i = 1; i <= 10; i++) {
      if (options.version == 4) {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_3/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
          class: i <= 5 ? "man" : "woman",
        });
      } else {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
          class: i <= 5 ? "man" : "woman",
        });
      }
    }
  }

  let template = `<div class="age-cta-4-group-${options.version}">`;

  images.forEach((image, index) => {
    template += `
    <a
      href="${options.url}"
      target="${options.tab}"
      ${image.class ? `class="${image.class}"` : ""}
    >
      <img src="${image.src}" alt="${image.alt}" data-image>
      ${
        options.version == 2
          ? `<span class="desktop">${image.title.desktop[index]}</span><span class="mobile">${image.title.mobile[index]}</span>`
          : `      <span>${image.title}</span>`
      }
    </a>`;
  });

  return template;
};

const getSkinnyAgeButtonsTemplate = options => {
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = '<div class="select-one-row select-one-row--small-mobile">';
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count} select-one-row__button--small-mobile"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count} select-one-row__button--small-mobile"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getTeamMembersTemplate = () => {
  const template = `
    <div data-shortcode='teammembers' class="avatar__row">
      <div class="avatar__box">
        <img class="avatar__image" src="${editor}" alt="Editor icon">
        <div class="avatar__title">Linda</div>
        <div class="avatar__subtitle">Editor &amp; Content Writer</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${developer}" alt="Developer icon">
        <div class="avatar__title">Luke</div>
        <div class="avatar__subtitle">Chief Software Developer</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${association_member}" alt="Association member icon">
        <div class="avatar__title">Steve</div>
        <div class="avatar__subtitle">Senior Association Member</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${analyst}" alt="Analyst icon">
        <div class="avatar__title">Klara</div>
        <div class="avatar__subtitle">Business Analyst &amp; Admin Assistant</div>
    </div>
  </div>
  `;

  return template;
};

const getReviewRatingsTemplate = () => {
  const template = `
  <div class="bg-gray-1 pb-3 pt-6 px-8 relative">
  <div class="md:flex">
    <div class="md:w-7/12">
      <div class="text-left text-gray-3 uppercase text-12 font-bold">
        Product rating
      </div>
      <div>
        <div class="justify-start flex mt-6 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:90%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">9</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Real Savings
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:100%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">10</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Discounts
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:80%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">8</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Price
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:90%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">9</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Easy To Use
          </div>
        </div>

      </div>
    </div>

    <div class="md:w-4/12 mx-auto mt-8 md:mt-0">
      <div class="text-left text-gray-3 uppercase text-12 font-bold">
        Total rating
      </div>
      <div class="mt-6 review-block--total-block text-center bg-white rounded-xl">
        <div class="review-block--total-rating text-45 font-bold text-green-1">
          9.2
        </div>
        <div class="text-center flex justify-center mt-2">
          <img src="${fourStars}">
          <span class="ml-3 my-auto text-12 review-block--total-ratings">
            (14,027)
          </span>
        </div>
        <div class="py-10">
          <a href="#" class="text-20 px-8 py-4 rounded-xl font-bold text-white bg-green-2 no-underline text-white cta-button">
            CTA
          </a>
        </div>
      </div>
    </div>
  </div>
</div>
  `;

  return template;
};

const getSocialSharerTemplate = () => {
  const template = `
  <div data-shortcode='socialsharer' class="social-sharer__container">
    <a class="social-sharer__item social-sharer--fb-blue"
      data-share='facebook'
      href="https://www.facebook.com/sharer/sharer.php?u="
      target="_blank"
    >
      Share on Facebook
    </a>
    <a class="social-sharer__item social-sharer--tw-blue"
      data-share='twitter'
      href="https://twitter.com/intent/tweet?url="
        target="_blank"
      >
      Tweet
    </a>
    <a class="social-sharer__item social-sharer--mail-white"
      data-share='mail'
      href="_blank">
      Share via Mail
    </a>
  </div>
`;

  return template;
};

const getMultiStepQuizWithLoaderTemplate = (message = "") => {
  return `
          <div data-multi-step-quiz-data='${message}' class="quiz-with-loader" data-tiny-mce-component="multistepquiz">
            <img src="${quizImage}" class="quiz-edit">
          </div>
        `;
};

// Displays a loader that has 3 steps.
const getMultiStepQuizV2WithLoaderTemplate = () => {
  return `<img src="${quizImage}" class="v2-quiz-edit">`;
};

// Displays a loader that only says "please wait..."
const getMultiStepQuizV2Template = () => {
  return `<img src="${quizImage}" class="v2-quiz-edit" data-no-loader="true">`;
};

const getMultiStepQuizV3Template = () => {
  return `<img src="${quizImageV3}" class="v3-quiz-edit">`;
};

const getMultiStepQuizV4Template = () => {
  return `<img src="${quizImageV4}" class="v4-quiz-edit">`;
};

const getVueMultiStepQuizV4Template = (quizData = "") => {
  return `<multistep-quiz-v4 :quiz-data='${JSON.stringify(quizData)}' class="v4-quiz-edit">`;
};

const getContentSplitTemplate = () => {
  return `<hr class="content-split-edit" />`;
};

const getVueMultiStepQuizWithLoader = (quizData = "") => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<multistep-quiz-with-loader :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz-with-loader>`;
};

const getMultiStepQuizTemplate = (message = "") => {
  return `
          <div data-multi-step-quiz-data='${message}' data-tiny-mce-component="multistepquiz">
            <img src="${quizImage}" class="quiz-edit">
          </div>
        `;
};

const getVueMultiStepQuiz = (quizData = "") => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<multistep-quiz :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz>`;
};

/**
 * TODO: maybe change the component name (?)
 */
const getVueMultiStepQuizV2 = (quizData = "", displayLoader = true) => {
  return `<multistep-quiz-v2 :quiz-data='${JSON.stringify(
    quizData,
  )}' :display-loader=${displayLoader}></multistep-quiz-v2>`;
};

const getVueMultiStepQuizV3 = (quizData = "") => {
  return `<multistep-quiz-v3 :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz-v3>`;
};

const getToastTemplate = () => {
  return `<img src="${toastImage}" class="toast-shortcode">`;
};
const getVueToastTemplate = (toastData = "", initialTime = "10000") => {
  return `<toast-component :toast-data='${JSON.stringify(toastData)}' :initial-time='${JSON.stringify(
    initialTime,
  )}'></toast-component>`;
};

const getLiveChatTemplate = () => {
  return `<img src="${livechatImage}" class="live-chat-shortcode">`;
};
const getLiveChatV2Template = () => {
  return `<img src="${livechatV2Image}" class="live-chat-v2-shortcode">`;
};
const getLiveChatAutoTemplate = () => {
  return `<img src="${livechatImage}" class="live-chat-auto-shortcode">`;
};

const getVueLiveChatTemplate = (liveChatData = "") => {
  return `<live-chat :live-chat-data='${JSON.stringify(liveChatData)}'></live-chat>`;
};
const getVueLiveChatV2Template = (liveChatData = "") => {
  return `<live-chat-v2 :live-chat-data='${JSON.stringify(liveChatData)}'></live-chat-v2>`;
};
const getVueLiveChatAutoTemplate = (liveChatData = "") => {
  return `<live-chat-auto :live-chat-data='${JSON.stringify(liveChatData)}'></live-chat-auto>`;
};
const getMultiStepFormTemplate = () => {
  return `<img src="${multiStepFormImage}" class="multi-step-form-shortcode">`;
};
const getVueMultiStepFormTemplate = (multiStepFormData = "") => {
  return `<multi-step-form :multi-step-form-data='${JSON.stringify(multiStepFormData)}'></multi-step-form>`;
};

const getLiveChatMp4Template = () => {
  return `<img src="${liveChatMp4Image}" class="live-chat-mp4-shortcode">`;
};
const getVueLiveChatMp4Template = (liveChatData = "") => {
  return `<live-chat-mp4 :live-chat-mp4-data='${JSON.stringify(liveChatData)}'></live-chat-mp4>`;
};
const getLiveChatMp4V2Template = () => {
  return `<img src="${liveChatMp4V2Image}" class="live-chat-mp4-v2-shortcode">`;
};
const getVueLiveChatMp4V2Template = (liveChatData = "") => {
  return `<live-chat-mp4-v2 :live-chat-mp4-data='${JSON.stringify(liveChatData)}'></live-chat-mp4-v2>`;
};

const getBottomBarTemplate = () => {
  return `<img src="${bottomBarImage}" class="bottom-bar-shortcode">`;
};
const getVueBottomBarTemplate = (bottomBarData = "") => {
  return `<bottom-bar :bottom-bar-data='${JSON.stringify(bottomBarData)}'></bottom-bar>`;
};

const getZipCodeTemplate = () => {
  return `<img src="${zipCodeImage}" class="zip-code-shortcode">`;
};

const getVueZipCodeTemplate = () => {
  return `<zip-code></zip-code>`;
};

const getAudioFormTemplate = () => {
  return `<img src="${audioFormImage}" class="audio-form-shortcode">`;
};

const getVueAudioFormTemplate = (audioFormData = "") => {
  return `<audio-form :audio-form-data='${JSON.stringify(audioFormData)}'></audio-form>`;
};

const getCommentsTemplate = (message = "") => {
  return `
            <div data-tiny-mce-component="landercomments" data-comments-widget-data='${message}'>
               <img class="comments-edit" src="${commentsWidgetImage}" >
            </div>
        `;
};

const getVueCommentsTemplate = commentsData => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<comments-widget :comments-data='${JSON.stringify(commentsData)}'></comments-widget>`;
};

const getCountdownTemplate = data => {
  const timerColor = data?.timerColor;
  const date = data?.selectedDate;

  const timeRemaining = calculateTimeRemaining(date);

  const countDownTemplate = `
    <div class="countdown-shortcode" data-countdown-date="${date}">
      <div class="countdown">
        <p style="text-align: center;"><strong>ANNUAL ENROLLMENT ENDS IN:</strong></p>
        <hr class="countdown-divider" data-editable="false" data-trigger-edit="true">
        <div class="countdown-items" data-editable="false" data-trigger-edit="true">
            <div class="countdown-item" data-type="days">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.days}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Days</div>
            </div>
            <div class="countdown-item" data-type="hours">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.hours}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Hours</div>
            </div>
            <div class="countdown-item" data-type="minutes">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.minutes}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Minutes</div>
            </div>
            <div class="countdown-item" data-type="seconds">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.seconds}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Seconds</div>
            </div>
        </div>
      </div>
    </div>
  `;

  return countDownTemplate;
};

const getDripFormTemplate = options => {
  const template = `
  <div data-tiny-mce-component="dripform">
    <div class="drip__container">
      <div
        data-tiny-mce-component-data="title"
        class="drip__title"
      >
        ${options?.title}
      </div>
      <div
        class="drip__description"
        data-tiny-mce-component-data="subtitle"
        >
           ${options?.subtitle}
      </div>
      <div class="drip__group">
        <div class="drip__group__first">
          <input
            v-model="email"
            type="email"
            class="drip__input"
            placeholder="Email Address"
          >
        </div>
        <div class="drip__group__second">
          <div class="drip__button">
            <span
            data-tiny-mce-component-data="button-text"
            >
               ${options?.buttonText}
            </span>
            <img
              src="${IMAGES_API}/images/misc/arrow.svg"
              data-image
              alt="Arrow"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
`;

  return template;
};

const getVueDripTemplate = ({ title, subtitle, buttonText }) => {
  const template = `
    <drip-form
      title="${title}"
      subtitle="${subtitle}"
      button-text=${buttonText}
    >
    </drip-form>
    `;

  return template;
};

const getCurrentDateTemplate = (dateFormat = "MMMM", offset = 0, locale = "en", variant = "default") => {
  return `{{ shortcodes.date("${dateFormat}", "${offset || 0}", "${locale || "en"}", "${variant || "default"}") }}`;
};

const getCurrentLocationtemplate = (countryFormat = "country_long") => {
  return '{{ shortcodes.geo("' + countryFormat + '") }}';
};

const getDynamicDomainNameTemplate = (
  domainType = DOMAIN_TYPES.FULL_URL,
  domainCase = CAPITALIZATION_VALUES.DEFAULT,
) => {
  let filter = "";
  let tld = ".com";
  switch (domainCase) {
    case CAPITALIZATION_VALUES.LOWER:
      filter = "|lower";
      break;
    case CAPITALIZATION_VALUES.UPPER:
      filter = "|upper";
      tld = ".COM";
      break;
  }

  return `{{ page.domain.name${filter} }}${domainType === DOMAIN_TYPES.FULL_URL ? tld : ""}`;
};

const getTimerTemplate = quantity => {
  return `<span style="color: #ff0000;" class="timeout">${quantity}</span>`;
};

const getVueTimer = quantity => {
  return `<timer quantity="${quantity}"></timer>`;
};

const getCounterTemplate = quantity => {
  return `<span class="counter">{${quantity}}</span>`;
};

const getVueCounter = quantity => {
  return `<counter quantity="${quantity}"></counter>`;
};

const getHrBreakLineTemplate = (color, width) => {
  return `
             <hr style="width:${width}; color: ${color}; border-color: ${color};">
          `;
};

const videoTemplate = `
      <div class="tox-dropzone-container" id="video-file-container">
          <div class="tox-dropzone">
              <button type="button" class="tox-button tox-button--dropzone">
                  Click to browse
                  <input type="file" id="video-file-input" accept="video/*" class="tox-input--file hidden-file-input" ">
              </button>
          </div>
      </div>
`;

const getTextHighlightTemplate = type => {
  return `
  <div class="${type} info-box">

  </div>
  `;
};

const getDynamicHighlightBoxTemplate = color => {
  return `
  <div style="background-color: ${color}" class="dynamic-info-box">

  </div>
  `;
};

const getDynamicHighlightBoxWithBorderTemplate = options => {
  return `
    <div style="background-color: ${options?.bgColor}; border-color: ${options?.borderColor}" class="dynamic-info-box__with-border">

    </div>
  `;
};

const getContactFormTemplate = () => {
  return `
    <div
      data-tiny-mce-component="contactform"
      id="contact-form"
      data-contact="true"
      autocomplete="off"
      class="w-full md:w-7/12 my-6"
    >
      <label
        class="block text-black-4 font-semibold mb-1"
        for="name"
      >
        Full Name
      </label>
      <div class="flex">
        <img
          class="px-3 pr-3 rounded border border-gray-5 z-20 -mr-2"
          src="${IMAGES_API}/images/icons/input_user_icon.svg"
          data-image
          alt="User icon"
        />
        <input
          id="name"
          required
          class="border-l-0 z-10 appearance-none block w-full text-gray-700 rounded py-4 px-4 leading-tight focus:outline-none focus:bg-white border border-gray-6"
          type="text"
        />
      </div>
      <label
        class="block text-black-4 font-semibold mb-1 mt-3"
        for="email"
      >
        Email Address
      </label>
      <div class="flex">
        <img
          class="px-3 pr-3 rounded border border-gray-5 z-20 -mr-2"
          src="${IMAGES_API}/images/icons/input_user_email.svg"
          data-image
          alt="User email icon"
        />
        <input
          id="email"
          required
          type="email"
          class="border-l-0 z-10 appearance-none block w-full text-gray-700 rounded py-4 px-4 leading-tight focus:outline-none focus:bg-white border border-gray-6"
          type="email"
        />
      </div>
      <div class="w-full">
        <label
          class="block text-black-4 font-semibold font-mb-1 mt-3"
          for="message"
        >
          Your Message
        </label>
      </div>
      <textarea
        id="message"
        required
        rows="4"
        cols="50"
        class="appearance-none block w-full text-gray-700 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white border border-gray-6"
        type="text"
      ></textarea>
      <div>
        <button
          type="submit"
          value="Submit"
          id="contact-form-button"
          class="text-20 font-extrabold bg-green-3 border-b-4 border-green-4 hover:bg-green-4 items-center w-full justify-center mt-3 mx-auto px-1 py-4 md:p-4 rounded-md text-white font-bold mb-4"
        >
          Send Message
        </button>
      </div>
      <div id="success-message" class="text-green-3" style="display: none;">
        Your message has been successfully sent
      </div>
    </div>
  `;
};

const getMultiButtonCtaWithImagesBox = options => {
  let template = "";
  if (options?.count < 5) {
    template += `
      <a href="${options?.customUrl}" target="${options?.openLink}">
        <div
          class="multi-button-cta-with-images cursor-pointer bg-blue-7 mt-2 md:mt-4 md:mt-0 mx-auto md:mx-0 flex rounded-4.7px max-w-33 md:min-w-33">
          <div>
            <img class="rounded-multi-image-shortcode w-78 min-w-78 h-86"
              src="${options?.imgSrc}"
              alt="${options?.imgAlt}"
              />
          </div>
          <div class="cursor-pointer underline text-white leading-29 text-21 font-bold font-arial pl-6 my-auto">
            ${options?.boxInput}
          </div>
          <div class="my-auto px-6 ml-auto">
            <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.2246 12.1502L0.238519 23.8312L0.238519 0.469171L11.2246 12.1502Z" fill="white" />
            </svg>
          </div>
        </div>
      </a>
    `;
  } else if (options?.count === 5) {
    template += `
        <a class="w-full" href="${options?.customUrl}" target="${options?.openLink}">
          <div
            class="multi-button-cta-with-images cursor-pointer bg-blue-7 mt-2 md:mt-4 md:mt-0 mx-auto md:mx-auto flex rounded-4.7px max-w-33 md:max-w-68">
            <div>
              <img class="rounded-multi-image-shortcode w-78 min-w-78 h-86"
                src="${options?.imgSrc}"
                alt="${options?.imgAlt}"
                />
            </div>
            <div class="cursor-pointer underline text-white leading-29 text-21 font-bold font-arial pl-6 my-auto">
              ${options?.boxInput}
            </div>
            <div class="my-auto px-6 ml-auto">
              <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.2246 12.1502L0.238519 23.8312L0.238519 0.469171L11.2246 12.1502Z" fill="white" />
              </svg>
            </div>
          </div>
        </a>
    `;
  }

  return template;
};

const getMultiButtonCtaWithImages = boxesInfo => {
  let template = `
    <div class="mx-auto px-6 md:px-0">
      <div class="md:flex md:space-x-8 mt-2 md:mt-4 md:justify-center">
    `;

  for (let x = 1; x <= boxesInfo?.quantity; x++) {
    const boxOptions = {
      count: x,
      customUrl: boxesInfo?.data?.customUrl,
      openLink: boxesInfo?.data?.openLink,
      imgAlt: boxesInfo?.data["imgAlt" + x],
      boxInput: boxesInfo?.data["boxInput" + x],
      imgSrc: boxesInfo?.data["imgSrc" + x]?.value,
    };
    if (x === 3) {
      template += `
        </div>
        <div class="md:flex md:space-x-8 mt-2 md:mt-4 justify-center">
      `;
    }
    if (x === 5) {
      template += `
        </div>
        <div class="md:flex md:space-x-8 mt-2 md:mt-4 justify-center">
      `;
    }
    template += getMultiButtonCtaWithImagesBox(boxOptions);
  }
  template += `
    </div>
  </div>
   `;

  return template;
};

const getVueContactTemplate = () => {
  const template = "<contact-form></contact-form>";

  return template;
};

const getUsStatesShortcodeTemplate = (states, options) => {
  let template = '<div class="md:flex md:flex-wrap">';
  states.forEach(state => {
    template += `
    <div class="w-full md:w-1/3 p-3">
      <a
        data-shortcode='buttons'
        class="button button--padding-fix button--${options.type} button-state"
        href="${options.url}"
        target="${options.tab}"
        >
        <span>${state.name}</span>
      </a>
    </div>`;
  });
  template += "</div>";

  return template;
};

const getCheckoutFormTemplate = () => {
  return `
    <div data-tiny-mce-component="checkoutform">
       <div class="max-w-67 mx-auto border-0 md:border-2 border-gray-4 rounded-xl py-12 md:py-16 px-6 md:px-28 font-robo">
            <form>
          <div class="text-38 text-center mb-12 md:mb-16 font-bold hidden md:block">
            Secure Order Form
          </div>
          <div>
            <div>
                <div class="relative pt-0 text-text-black">
                    <div class="text-24 text-blue-8 font-medium mb-12 md:mb-16">
                        Personal Information
                    </div>
                    <div>
                        <div>
                            <div class="md:flex md:-mx-4">
                                <div class="md:mx-4 w-full md:w-2/4 mb-10">
                                    <div class="">
                                        <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                            First Name*
                                        </label>
                                        <div class="flex flex-grow justify-center group">
                                            <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                                    </div>
                                    </div>
                                 </div>
                                <div class="md:mx-4 w-full md:w-2/4 mb-10">
                                   <div class="">
                                      <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                          Last Name*
                                      </label>
                                      <div class="flex flex-grow justify-center group">
                                        <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                                    </div>
                                   </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="">
                    <div class="text-24 text-blue-8 font-medium mb-12 md:mb-16">
                        Billing Address
                    </div>
                    <div class="mb-8">
                          <div class="">
                              <label for="cardHolder" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Address*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                  <input type="text" placeholder="" id="cardHolder" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                              </div>
                          </div>
                    </div>
                    <div class="md:flex md:-mx-4">
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                            <div class="">
                                <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                    City*
                                </label>
                                <div class="flex flex-grow justify-center group">
                                    <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                            </div>
                         </div>
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                           <div class="">
                              <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Country*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                           </div>
                        </div>
                    </div>
                    <div class="md:flex md:-mx-4">
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                            <div class="">
                                <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                    State*
                                </label>
                                <div class="flex flex-grow justify-center group">
                                    <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                            </div>
                         </div>
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                           <div class="">
                              <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Postal Code*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                           </div>
                        </div>
                    </div>
                </div>
          </div>
          <div class="relative">
              <div class="text-24 text-blue-8 font-medium mt-2 md:mt-6 mb-12 md:mb-16 ">
                    Payment Information
              </div>
              <div>
                  <div>
                      <div>
                          <div>
                              <div class="mb-8">
                                  <div class="">
                                      <label for="cardHolder" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                          Card Holder Name*
                                      </label>
                                      <div class="flex flex-grow justify-center group">
                                          <input type="text" placeholder="" id="cardHolder" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                      </div>
                                  </div>
                              </div>
                              <div class="mb-8 mt-6 md:mt-0">
                                  <div class="relative">
                                      <label for="cardNumber" class="flex items-center text-black-702 text-16 leading-25 font-bold">
                                          Card Number*
                                          <img src="${cards}" alt="cards" class="ml-2 mb-2 h-12">
                                      </label>
                                      <div class="relative">
                                          <input type="text" value="" id="cardNumber" inputmode="tel" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                      </div>
                                  </div>
                                  <div class="md:flex mt-8">
                                      <div class="md:w-1/2 mr-64 md:mr-8 mb-10 md:mb-0">
                                        <div>
                                            <div>
                                                <label for="card" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                                    Expiry Date*
                                                </label>
                                                <div class="flex justify-between items-baseline">
                                                    <div class="custom-width-50">
                                                        <select id="" class="checkout-select">
                                                            <option disabled="disabled">Month</option>
                                                            <option value="01">01</option>
                                                            <option value="02">02</option>
                                                            <option value="03"> 03</option>
                                                            <option value="04">04</option>
                                                        </select>
                                                    </div>
                                                    <span class="text-20">/</span>
                                                    <div class="custom-width-50">
                                                        <select id="" class="checkout-select">
                                                            <option disabled="disabled" hidden="hidden">Year</option>
                                                            <option value="2021">2021</option>
                                                            <option value="2022">2022</option>
                                                            <option value="2023">2023</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                      <div class="md:w-1/2 mb-6 md:mb-0 flex">
                                        <div class="relative flex-1">
                                            <div class="">
                                                <label for="cardSecurityCode" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                                    CVV/CVC*
                                                </label>
                                            <div class="flex flex-grow justify-center group">
                                                <input type="text" placeholder="" id="cardSecurityCode" maxlength="4" inputmode="tel" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                            </div>
                                        </div>
                                    </div>
                                    <div class=" min-w-64px">
                                        <img src="${cvvIcon}" alt="cvv" class="ml-2 mb-2 mt-16">
                                    </div>
                                </div>
                                  </div>
                      </div>
                  </div>
              </div>
          </div>
          <div class="pt-4 mx-auto text-left">
              <div class="pb-3 text-15 font-semibold text-black ">
                  Order summary
              </div>
              <div class="flex text-15 pb-2 text-black-704">
                  <div>Lifetime Membership</div>
                  <div class="ml-auto line-through">$199</div>
              </div>
              <div class="flex text-15 pb-3 text-black-704"><div>
                Discount -80%
              </div>
              <div class="ml-auto text-red-700 font-semibold">-$160</div>
          </div>
          <div class="flex pb-8 text-bold">
              <div class="font-bold text-16">Total</div>
              <div class="ml-auto text-16 font-bold">USD $39</div>
          </div>
          <div class="text-15 text-black-704">
                This is a one-time purchase.  No subscriptions or monthly charges. No hidden fees.
          </div>
          <div>
              <button type="button" class="text-24 leading-46 block font-bold text-white py-6 px:8 md:px-16 mx-auto rounded-lg bg-green-10">
                      Submit My Order
              </button>
          </div>
       </div>
    <div class="my-12 flex justify-center items-center">
          <img src="${guarantee}" alt="" class="mr-8 max-w-xl">
              <div class="leading-26 text-14 font-open text-black-705">
                  This is a one-time payment, does not auto-renew.
                  No subscriptions, no hidden fees.
                  You’re 100% backed by our 30-Day Money-Back Guarantee.
              </div>
          </div>
          <div class="px-6 mx-auto max-w-3xl">
          <div class="text-3xl text-center mb-2">
              <span class="font-bold text-green-700">
                  <img class="inline" src="${greenLock}" alt="green lock icon">
                SSL
              </span>
              SECURED PAYMENT
          </div>
          <div class="text-10 md:text-12 text-center">
              Your information is protected by 256-bit SSL encryption
          </div>
      </div>
      </div>
      </form>
       </div>
    </div>
  `;
};

const getVueCheckoutFormTemplate = () => {
  return `
    <checkout-form></checkout-form>
  `;
};

const getEmailFormTemplate = () => {
  return `
    <div data-tiny-mce-component='emailform'>
        <label
          for="email_address"
          class="text-black-4 text-18 leading-28"
          data-tiny-mce-component-data="label"
        >
        Enter Email Address:
        </label>
        <div class="">
            <input id="email_address" type="text" class="border border-gray-13 rounded-md p-3.5 leading-26 min-w-26">
        </div>
        <div class="mt-8">
          <div
              class="text-10 leading-14 inline"
              data-tiny-mce-component-data="text"
          >
              By submitting your email address, you consent to us keeping you informed about updates to our website and about other products and services that we think might interest you. You can unsubscribe at any time. Please read our
          </div>&nbsp;
          <a href=""
             class="text-blue-8 text-10 leading-14"
             target="_self"
             data-tiny-mce-component-data="link"
             >
             Privacy Statement Terms & Conditions
          </a>
        </div>
        <div class="mt-4">
            <button class="text-center text-26 font-bold text-white rounded-6 py-8 px-24 leading-none shadow-gray-bottom bg-gradient-to-b from-green-9 to-green-11 md:hover:bg-gradient-to-t md:hover:from-green-11 md:hover:to-green-9"
                    type="submit"
                    data-tiny-mce-component-data="buttonText"
            >
                Continue »
            </button>
        </div>
    </div>
  `;
};

const getVueEmailFormTemplate = ({ label, text, buttonText, linkText, linkHref, linkTarget }) => {
  const template = `
    <email-form
      label="${label}"
      text="${text}"
      button-text=${buttonText}
      link-text="${linkText}"
      link-href="${linkHref}"
      link-target="${linkTarget}"
    >
    </email-form>
    `;

  return template;
};

const abSplitTest = () => {
  return `
    <div data-split-test="" class="py-4">
        <hr>
    </div>
  `;
};

const getInnerRingbaPhoneNumberTemplate = options => {
  let image = null;

  if (options?.buttonType === "button") {
    image = ringbaPhoneNumberButtonImage;
  } else if (options?.buttonType === "link-with-button") {
    image = ringbaPhoneNumberLinkAndButtonImage;
  }

  return `
    <div class="ringba-phone-number-edit-container" style="pointer-events: none; position: absolute; top: 35px; left: 10px; text-align: left; font-size: 14px;">
      <div class="ringba-phone-number-edit">
        ${options?.ringbaPhoneNumber?.text}
      </div>
    </div>
    <img src="${image}" class="ringba-phone-number-edit">
  `;
};

const getRingbaPhoneNumberTemplate = options => {
  return `
    <div
      data-tiny-mce-component="ringba-phone-number"
      class="ringba-phone-number"
      data-ringba-phone-number=''
    >
      ${getInnerRingbaPhoneNumberTemplate(options)}
    </div>
  `;
};

const getHtmlRingbaPhoneNumberTemplate = options => {
  let content = null;
  if (options?.buttonType === "link-with-button") {
    content = `
      <a
        class="ringba-phone-number__link"
        href="tel:%${options?.ringbaPhoneNumber?.value}:raw%"
      >
        %${options?.ringbaPhoneNumber?.value}:formatted%
      </a>
      <a class="button ringba-phone-number__button" href="tel:%${options?.ringbaPhoneNumber?.value}:raw%">
        Call Now!
      </a>`;
  } else {
    content = `
      <a
        class="button ringba-phone-number__button"
        href="tel:%${options?.ringbaPhoneNumber?.value}:raw%"
      >
        %${options?.ringbaPhoneNumber?.value}:formatted%
      </a>
    `;
  }

  return `
    <div
      data-tiny-mce-component="ringba-phone-number"
      class="ringba-phone-number"
      data-ringba-phone-number='${JSON.stringify(content)}'
    >
      ${content}
    </div>
  `;
};

const getAnimatedButtonTemplate = options => {
  return `<a
          onMouseOver="this.style.backgroundColor='${options?.hoverColor}'"
          onMouseOut="this.style.backgroundColor='${options?.bgColor}'"
          style="background-color: ${options?.bgColor}"
          class="animated-button-shortcode"
          href="${options?.url}"
          ${options?.tab ? `target="${options.tab}"` : ""}
          data-btn-type="${options?.type}"
        >
        <div class="content-wrapper">
          ${
            options?.type === "call"
              ? ` <div>
                <img class="animated-button-shortcode__phone" src="${IMAGES_API}/images/phone.png" alt="Phone"/>
              </div>`
              : ``
          }
          <div class="animated-button-shortcode__text">
            ${options?.text}
          </div>
        </div>
        <img class="animated-button-shortcode__tap" src="${IMAGES_API}/images/tap.gif" alt="Tap"/>
  </a>`;
};

const getCarrierNamesOtcTemplate = () => {
  return `<span class="carrier-names" type="otc">{Carrier Names Otc}</span>`;
};
const getCarrierNamesPartBTemplate = () => {
  return `<span class="carrier-names" type="partb">{Carrier Names Part B}</span>`;
};

const getVueCarrierNames = type => {
  return `<carrier-names type="${type}"></carrier-names>`;
};

const getAgeInputQuizTemplate = () => {
  return `<img src="${ageQuizImage}" class="vue-age-input">`;
};

const getVueAgeInputQuizTemplate = (quizData = "") => {
  return `<age-input-quiz age-input-data='${quizData}' class="vue-age-input"></age-input-quiz>`;
};

const getSpinWheelTemplate = () => {
  return `<img src="${spinWheelImage}" class="vue-spin-wheel">`;
};

const getVueSpinWheelTemplate = (spinWheelData = "") => {
  return `<spin-wheel spin-wheel-data='${spinWheelData}' class="vue-spin-wheel"></spin-wheel>`;
};

const getLiveClaimFeedTemplate = () => {
  return `<img src="${liveClaimFeedImage}" class="vue-live-claim-feed">`;
};

const getVueLiveClaimFeedTemplate = (feedData = "") => {
  return `<live-claim-feed live-claim-feed-data='${feedData}' class="vue-live-claim-feed"></live-claim-feed>`;
};

const getContactUsFormTemplate = (options = {}) => {
  const defaults = {
    sidebarTitle: "Contact Information",
    sidebarSubtitle: "Say something to start a live chat!",
    phone: "+1012 3456 789",
    email: "demo@gmail.com",
    address: "132 Dartmouth Street Boston, Massachusetts 02156 United States",
    company: "Improve Insurance Company",
    buttonText: "Send Message",
    buttonBgColor: "#1B1B1B",
    buttonTextColor: "#FFFFFF",
    showSideBlock: true,
    sideBlockBgColor: "#1B1B1B",
    sideBlockTextColor: "#FFFFFF",
    sendEmailToGroove: false,
    successMessage: "Your message sent!",
    errorMessage: "Your message cannot be sent.",
  };

  const opts = { ...defaults, ...options };

  const sidebarHtml = opts.showSideBlock
    ? `
    <div class="contact-us-form__sidebar" style="background-color: ${opts.sideBlockBgColor}; color: ${opts.sideBlockTextColor};">
      <div class="contact-us-form__sidebar-content">
        <div class="contact-us-form__sidebar-title" data-tiny-mce-component-data="sidebar-title">${opts.sidebarTitle}</div>
        <div class="contact-us-form__sidebar-subtitle" data-tiny-mce-component-data="sidebar-subtitle">${opts.sidebarSubtitle}</div>
        <div class="contact-us-form__sidebar-items">
          <div class="contact-us-form__sidebar-item">
            <svg class="contact-us-form__sidebar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.3333 14.1V16.6C18.3343 16.8321 18.2867 17.0618 18.1937 17.2745C18.1008 17.4871 17.9644 17.678 17.7934 17.8349C17.6224 17.9918 17.4205 18.1113 17.2006 18.1856C16.9808 18.26 16.7478 18.2876 16.5167 18.2667C13.9523 17.988 11.489 17.1118 9.32499 15.7083C7.31151 14.4289 5.60443 12.7218 4.32499 10.7083C2.91663 8.53438 2.04019 6.05917 1.76666 3.48334C1.74583 3.25293 1.77321 3.02067 1.84707 2.80139C1.92092 2.58211 2.03963 2.38061 2.19562 2.2096C2.35162 2.03859 2.54149 1.90195 2.75314 1.80852C2.9648 1.71508 3.19348 1.66693 3.42499 1.66667H5.92499C6.32941 1.66273 6.72148 1.80594 7.02812 2.06965C7.33476 2.33336 7.53505 2.69958 7.59166 3.1C7.69717 3.9001 7.89286 4.68565 8.17499 5.44167C8.2871 5.73997 8.31137 6.06412 8.24491 6.37577C8.17844 6.68741 8.02404 6.97347 7.79999 7.2L6.74166 8.25834C7.92795 10.3447 9.65536 12.0721 11.7417 13.2583L12.8 12.2C13.0265 11.976 13.3126 11.8216 13.6242 11.7551C13.9359 11.6886 14.26 11.7129 14.5583 11.825C15.3144 12.1071 16.0999 12.3028 16.9 12.4083C17.3048 12.4655 17.6745 12.6694 17.9388 12.9813C18.203 13.2932 18.3435 13.6914 18.3333 14.1Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span data-tiny-mce-component-data="phone">${opts.phone}</span>
          </div>
          <div class="contact-us-form__sidebar-item">
            <svg class="contact-us-form__sidebar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.33334 3.33334H16.6667C17.5833 3.33334 18.3333 4.08334 18.3333 5.00001V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33334C2.41667 16.6667 1.66667 15.9167 1.66667 15V5.00001C1.66667 4.08334 2.41667 3.33334 3.33334 3.33334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span data-tiny-mce-component-data="email">${opts.email}</span>
          </div>
          <div class="contact-us-form__sidebar-item">
            <svg class="contact-us-form__sidebar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.5 8.33334C17.5 14.1667 10 19.1667 10 19.1667C10 19.1667 2.5 14.1667 2.5 8.33334C2.5 6.34422 3.29018 4.43656 4.6967 3.03004C6.10322 1.62352 8.01088 0.833344 10 0.833344C11.9891 0.833344 13.8968 1.62352 15.3033 3.03004C16.7098 4.43656 17.5 6.34422 17.5 8.33334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 10.8333C11.3807 10.8333 12.5 9.71406 12.5 8.33334C12.5 6.95263 11.3807 5.83334 10 5.83334C8.61929 5.83334 7.5 6.95263 7.5 8.33334C7.5 9.71406 8.61929 10.8333 10 10.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span data-tiny-mce-component-data="address">${opts.address}</span>
          </div>
          <div class="contact-us-form__sidebar-item">
            <svg class="contact-us-form__sidebar-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 7.5L10 1.66667L17.5 7.5V16.6667C17.5 17.1087 17.3244 17.5326 17.0118 17.8452C16.6993 18.1577 16.2754 18.3333 15.8333 18.3333H4.16667C3.72464 18.3333 3.30072 18.1577 2.98816 17.8452C2.67559 17.5326 2.5 17.1087 2.5 16.6667V7.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7.5 18.3333V10H12.5V18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span data-tiny-mce-component-data="company">${opts.company}</span>
          </div>
        </div>
      </div>
    </div>`
    : "";

  const noSidebarClass = !opts.showSideBlock ? " contact-us-form--no-sidebar" : "";

  return `
    <div data-tiny-mce-component="contact-us-form" class="contact-us-form${noSidebarClass}">
      ${sidebarHtml}
      <div class="contact-us-form__form-container">
        <div class="contact-us-form__form">
          <div class="contact-us-form__field">
            <label class="contact-us-form__label">${opts.nameLabel}</label>
            <input type="text" class="contact-us-form__input" placeholder="${opts.namePlaceholder}" disabled />
          </div>
          <div class="contact-us-form__field">
            <label class="contact-us-form__label">${opts.emailLabel}</label>
            <input type="email" class="contact-us-form__input" placeholder="${opts.emailPlaceholder}" disabled />
          </div>
          <div class="contact-us-form__field">
            <label class="contact-us-form__label">${opts.messageLabel}</label>
            <textarea class="contact-us-form__textarea" placeholder="${opts.messagePlaceholder}" disabled></textarea>
          </div>
          <button type="button" class="contact-us-form__button" style="background-color: ${opts.buttonBgColor}; color: ${opts.buttonTextColor};">
            <span data-tiny-mce-component-data="button-text">${opts.buttonText}</span>
          </button>
        </div>
      </div>
    </div>
  `;
};

const getVueContactUsFormTemplate = (formData = "") => {
  return `<contact-us-form contact-us-form-data='${formData}' class="vue-contact-us-form"></contact-us-form>`;
};

const getImageBannerTemplate = () => {
  return `<img src="${imageBannerImage}" class="vue-image-banner">`;
};

const getVueImageBannerTemplate = (imageBannerData = "") => {
  return `<image-banner image-banner-data='${imageBannerData}' class="vue-image-banner"></image-banner>`;
};

const getSocialProofTemplate = () => {
  return `<img src="${socialProofImage}" class="vue-social-proof">`;
};

const getVueSocialProofTemplate = (socialProofData = "") => {
  return `<social-proof social-proof-data='${socialProofData}' class="vue-social-proof"></social-proof>`;
};

const getSocialProofV2Template = () => {
  return `<img src="${socialProofV2Image}" class="vue-social-proof-v2">`;
};

const getVueSocialProofV2Template = (socialProofV2Data = "") => {
  return `<social-proof-v2 social-proof-v2-data='${socialProofV2Data}' class="vue-social-proof-v2"></social-proof-v2>`;
};

const getDynamicButtonsTemplate = (options = {}) => {
  const { buttons = [], globalLink = "#", mobileColumns = 1, desktopColumns = 2 } = options;
  const mobileCols = mobileColumns || 1;
  const desktopCols = desktopColumns || 2;

  let template = `
    <div
      class="dynamic-buttons-container"
      data-shortcode="buttons"
      style="--mobile-columns: ${mobileCols}; --desktop-columns: ${desktopCols};"
    >`;

  buttons.forEach(button => {
    const link = button.link || globalLink;
    const backgroundColor = button.backgroundColor || "#28b02b";
    const textColor = button.textColor || "#ffffff";
    const shadowColor = button.shadowColor || button.backgroundColor || "#1a7a1c";

    template += `
      <div>
        <a
          href="${link}"
          target="${button.target || "_self"}"
          style="text-decoration: none !important; color: inherit; display: block;"
        >
            <button
              type="button"
              style="position: relative; font-size: 24px; display: block; width: 100%; height: clamp(54px, 7vh, 74px); border: 0px; border-bottom: 6px solid ${shadowColor}; cursor: pointer; background: ${backgroundColor}; color: ${textColor}; border-radius: 9999px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; line-height: 1; box-shadow: rgba(0, 0, 0, 0.35) 0px 14px 28px; text-decoration: none;"
            >
              ${button.text || "Button"}
            </button>
        </a>
      </div>
    `;
  });

  template += "</div>";
  return template;
};

const getNumberedStepsTemplate = () => {
  return `<img src="${numberedStepsImage}" class="vue-numbered-steps">`;
};

const getVueNumberedStepsTemplate = (data = "") => {
  return `<numbered-steps numbered-steps-data='${data}' class="vue-numbered-steps"></numbered-steps>`;
};

const getWarningBannerTemplate = options => {
  const labelText = options?.labelText || "WARNING:";
  const contentText =
    options?.contentText ||
    'Enrollment closes on {{ shortcodes.date("eeee", "0") }}, {{ shortcodes.date("MM/dd/yyyy", "0") }} at midnight.';
  const labelBgColor = options?.labelBgColor || "#C41230";
  const contentBgColor = options?.contentBgColor || "#002855";
  const textColor = options?.textColor || "#FFFFFF";

  return `
    <div class="warning-banner" style="background-color: ${contentBgColor};">
      <div class="warning-banner__label" style="background-color: ${labelBgColor};">
        <span class="warning-banner__label-text" style="color: ${textColor};">${labelText}</span>
      </div>
      <div class="warning-banner__content">
        <span class="warning-banner__content-text" style="color: ${textColor};">${contentText}</span>
      </div>
    </div>
  `;
};

const getSmallTextBoxTemplate = (options = {}) => {
  const { text, textColor, borderColor, bgColor, fontSize, borderWidth, alignment } = options;

  return `
    <div class="small-text-box" style="text-align: ${alignment};" data-editable="false">
      <span class="small-text-box__inner" style="color: ${textColor}; border-color: ${borderColor}; border-width: ${borderWidth}px; background-color: ${bgColor}; font-size: ${fontSize}px;">${text}</span>
    </div>
  `;
};

const getContentBoxRowsTemplate = (options = {}) => {
  const {
    enableMaxWidth = false,
    maxWidth = 770,
    rows = [
      {
        columns: [
          {
            content:
              "<h3>It Only Takes 60 Seconds To Check If You Qualify!</h3><p>Thousands of seniors have already checked their eligibility. Don't leave money you're entitled to unclaimed.</p>",
          },
        ],
      },
    ],
  } = options;

  const maxWidthStyle = enableMaxWidth ? ` style="max-width: ${maxWidth}px;"` : "";

  return rows
    .map(row => {
      const columnsHtml = row.columns
        .map(column => {
          const width = Number.parseFloat(column.width);

          if (!Number.isFinite(width) || width <= 0) {
            return `<div class="content-box__column">${column.content}</div>`;
          }

          return `<div class="content-box__column content-box__column--sized" style="--column-width: ${width}%;">${column.content}</div>`;
        })
        .join("");

      return `<div class="content-box__columns"${maxWidthStyle}>${columnsHtml}</div>`;
    })
    .join("");
};

const isContentBoxSideOn = (box, side) => "none" !== box.borderStyle && box.borderWidth[side] > 0;

const isContentBoxLine = (box, side) => isContentBoxSideOn(box, side) && box.lineLength < 100;

const isContentBoxRealBorder = (box, side) => isContentBoxSideOn(box, side) && !isContentBoxLine(box, side);

const getContentBoxBorderWidth = box =>
  CONTENT_BOX_BORDER_SIDES.map(side => (isContentBoxRealBorder(box, side) ? `${box.borderWidth[side]}px` : "0px")).join(
    " ",
  );

const getContentBoxBorderStyle = box =>
  CONTENT_BOX_BORDER_SIDES.map(side => (isContentBoxRealBorder(box, side) ? box.borderStyle : "none")).join(" ");

// The two sides meeting at each corner, in `border-radius` shorthand order.
const CONTENT_BOX_CORNERS = [
  ["top", "left"],
  ["top", "right"],
  ["bottom", "right"],
  ["bottom", "left"],
];

// A border's inner edge curves by (radius - border width), which squares off the content
// area under a thick border. Every corner therefore carries the width of its own two sides
// on top of the picked radius, leaving the content area rounded by exactly that radius
// while a corner between two borderless sides keeps it untouched.
const getContentBoxRadius = box => {
  if (!box.cornerRadius) return "0px";

  return CONTENT_BOX_CORNERS.map(corner => {
    const widths = corner.map(side => (isContentBoxRealBorder(box, side) ? box.borderWidth[side] : 0));

    return `${box.cornerRadius + Math.max(...widths)}px`;
  }).join(" ");
};

// Encoding has to match the api decoder (see socialProofV2Config).
const CONTENT_BOX_DATA_ATTRIBUTE = "data-content-box-data";

// Columns are edited in place, so the DOM owns their content and widths.
const encodeContentBoxData = ({ rows, ...config }) => window.btoa(escape(encodeURIComponent(JSON.stringify(config))));

const decodeContentBoxData = value => JSON.parse(decodeURIComponent(unescape(window.atob(value))));

const getContentBoxPadding = box =>
  CONTENT_BOX_BORDER_SIDES.map(side => {
    const line = isContentBoxLine(box, side) ? box.borderWidth[side] : 0;

    return `${box.padding[side] + line}px`;
  }).join(" ");

const getContentBoxLineDeclarations = (prefix, box) => {
  const declarations = [
    `${prefix}line-length: ${box.lineLength}%`,
    `${prefix}line-style: ${"none" === box.borderStyle ? "solid" : box.borderStyle}`,
    `${prefix}line-color: ${box.borderColor}`,
  ];

  CONTENT_BOX_BORDER_SIDES.forEach(side => {
    declarations.push(`${prefix}line-${side}-width: ${isContentBoxLine(box, side) ? box.borderWidth[side] : 0}px`);
  });

  return declarations;
};

const CONTENT_BOX_FULL_WIDTH_CLASSES = {
  both: "content-box--full-width",
  desktop: "content-box--full-width-desktop",
  mobile: "content-box--full-width-mobile",
};

const getContentBoxFullWidthClass = ({ fullWidth = false, separateMobile = false, mobile = {} } = {}) => {
  const onMobile = separateMobile ? Boolean(mobile.fullWidth) : Boolean(fullWidth);

  if (fullWidth && onMobile) return CONTENT_BOX_FULL_WIDTH_CLASSES.both;
  if (fullWidth) return CONTENT_BOX_FULL_WIDTH_CLASSES.desktop;
  if (onMobile) return CONTENT_BOX_FULL_WIDTH_CLASSES.mobile;

  return "";
};

// Mobile rides along as custom properties, which TinyMCE persists via `data-mce-style`.
const CONTENT_BOX_MOBILE_CLASS = "content-box--mobile";

const CONTENT_BOX_SHADOW = "0 1px 4px rgba(20, 20, 26, 0.1)";

const getContentBoxStyle = (box = {}) => {
  if (!Object.keys(box).length) return "";

  const { mobile } = box;

  const declarations = [
    `border-style: ${getContentBoxBorderStyle(box)}`,
    `border-color: ${box.borderColor}`,
    `border-width: ${getContentBoxBorderWidth(box)}`,
    `border-radius: ${getContentBoxRadius(box)}`,
    `background-color: ${box.enableBackground ? box.backgroundColor : "transparent"}`,
    `box-shadow: ${box.enableBackground ? CONTENT_BOX_SHADOW : "none"}`,
    `padding: ${getContentBoxPadding(box)}`,
    `margin-top: ${box.margin.top}px`,
    `margin-bottom: ${box.margin.bottom}px`,
    `--content-box-margin-left: ${box.margin.left}px`,
    `--content-box-margin-right: ${box.margin.right}px`,
    ...getContentBoxLineDeclarations("--content-box-", box),
  ];

  if (box.separateMobile) {
    declarations.push(
      `--content-box-mobile-border-style: ${getContentBoxBorderStyle(mobile)}`,
      `--content-box-mobile-border-color: ${mobile.borderColor}`,
      `--content-box-mobile-border-width: ${getContentBoxBorderWidth(mobile)}`,
      `--content-box-mobile-radius: ${getContentBoxRadius(mobile)}`,
      `--content-box-mobile-background: ${mobile.enableBackground ? mobile.backgroundColor : "transparent"}`,
      `--content-box-mobile-shadow: ${mobile.enableBackground ? CONTENT_BOX_SHADOW : "none"}`,
      `--content-box-mobile-margin-top: ${mobile.margin.top}px`,
      `--content-box-mobile-margin-bottom: ${mobile.margin.bottom}px`,
      `--content-box-mobile-margin-left: ${mobile.margin.left}px`,
      `--content-box-mobile-margin-right: ${mobile.margin.right}px`,
      `--content-box-mobile-padding: ${getContentBoxPadding(mobile)}`,
      ...getContentBoxLineDeclarations("--content-box-mobile-", mobile),
    );
  }

  return declarations.join("; ") + ";";
};

const getContentBoxTemplate = (options = {}) => {
  const classes = [
    "content-box",
    "dash-highlight",
    options.separateMobile ? CONTENT_BOX_MOBILE_CLASS : "",
    getContentBoxFullWidthClass(options),
  ]
    .filter(Boolean)
    .join(" ");

  return `
    <div class="${classes}" ${CONTENT_BOX_DATA_ATTRIBUTE}="${encodeContentBoxData(
    options,
  )}" style="${getContentBoxStyle(options)}">
      ${getContentBoxRowsTemplate(options)}
    </div>
  `;
};

const TEXT_UNDERLINE_ALIGNMENTS = ["left", "center", "right"];

const sanitizeCssColor = (value, fallback = "#2454C7") => {
  const v = String(value ?? "").trim();
  if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v;
  if (/^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+)?\s*\)$/i.test(v)) return v;
  return fallback;
};

const sanitizePositivePx = (value, fallback, max) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(n, max);
};

const getTextUnderlineTemplate = (options = {}) => {
  const alignment = TEXT_UNDERLINE_ALIGNMENTS.includes(options.alignment) ? options.alignment : "left";
  const lineColor = sanitizeCssColor(options.lineColor);
  const lineWeight = sanitizePositivePx(options.lineWeight, 2, 40);
  const lineLength = sanitizePositivePx(options.lineLength, 140, 1200);
  const text = options.text ?? "";

  return `
    <div class="text-underline" style="text-align: ${alignment};" data-editable="false">
      <div class="text-underline__text">${text}</div>
      <div class="text-underline__line" style="background-color: ${lineColor}; width: ${lineLength}px; height: ${lineWeight}px;"></div>
    </div>
  `;
};

const getNewsletterBannerTemplate = () => {
  return `<img src="${newsletterBannerImage}" class="vue-newsletter-banner">`;
};

const getVueNewsletterBannerTemplate = (data = "") => {
  return `<newsletter-banner newsletter-banner-data='${data}' class="vue-newsletter-banner"></newsletter-banner>`;
};

export {
  getCTATemplate,
  getShortCTATemplate,
  getShortCTA4Template,
  getCustomButtonTemplate,
  getMultipleCTATemplate,
  getQuoteBoxTemplate,
  getQuoteTemplate,
  getSelectAgeTemplate,
  getSelectAgeIconsTemplate,
  getMileageTemplate,
  getTeamMembersTemplate,
  getReviewRatingsTemplate,
  getCurrentDateTemplate,
  getCurrentLocationtemplate,
  getDripFormTemplate,
  getVueDripTemplate,
  getSocialSharerTemplate,
  getCommentsTemplate,
  getVueCommentsTemplate,
  getCountdownTemplate,
  getContactFormTemplate,
  getVueContactTemplate,
  getTextHighlightTemplate,
  getCheckoutPlanTemplate,
  getUsStatesShortcodeTemplate,
  getCTAIndyTemplate,
  getCTAOuterTemplate,
  getGifAgeTemplate,
  getHrBreakLineTemplate,
  getMultiButtonCta,
  getMultiButtonCtaWithImages,
  getWhiteMultiButtonCta,
  getCTATemplateWidthAd,
  getCheckoutFormTemplate,
  getVueCheckoutFormTemplate,
  getEmailFormTemplate,
  getVueEmailFormTemplate,
  getSelectAgeInOneRowTemplate,
  getAgeCTA3Template,
  getSkinnyAgeButtonsTemplate,
  getMultiStepQuizTemplate,
  getMultiStepQuizWithLoaderTemplate,
  getMultiStepQuizV2WithLoaderTemplate,
  getMultiStepQuizV2Template,
  getVueMultiStepQuiz,
  getVueMultiStepQuizV2,
  getVueMultiStepQuizWithLoader,
  getDynamicHighlightBoxTemplate,
  getDynamicHighlightBoxWithBorderTemplate,
  getCtaButtonNumberTemplate,
  getCTAPhoneTemplate,
  getMultiButtonCtaWithSeparateLinks,
  getDynamicColorMultiButtonCta,
  getShortcodeHTMLTemplate,
  getTimerTemplate,
  getVueTimer,
  abSplitTest,
  getContentSplitTemplate,
  getToastTemplate,
  getVueToastTemplate,
  getLiveChatTemplate,
  getVueLiveChatTemplate,
  getLiveChatV2Template,
  getVueLiveChatV2Template,
  getBottomBarTemplate,
  getVueBottomBarTemplate,
  getZipCodeTemplate,
  getVueZipCodeTemplate,
  getAudioFormTemplate,
  getVueAudioFormTemplate,
  getAnimatedButtonTemplate,
  getVueCounter,
  getCounterTemplate,
  getCarrierNamesOtcTemplate,
  getCarrierNamesPartBTemplate,
  getVueCarrierNames,
  getMultiStepQuizV3Template,
  getVueMultiStepQuizV3,
  getMultiStepQuizV4Template,
  getVueMultiStepQuizV4Template,
  getMultiButtonCtaV3Template,
  getMoneyCTATemplate,
  getMultiButtonCtaV4Template,
  getButtonsWithTextBoxTemplate,
  getButtonsWithTextBoxVueTemplate,
  getLiveChatAutoTemplate,
  getVueLiveChatAutoTemplate,
  getMultiStepFormTemplate,
  getVueMultiStepFormTemplate,
  getLiveChatMp4Template,
  getVueLiveChatMp4Template,
  getLiveChatMp4V2Template,
  getVueLiveChatMp4V2Template,
  getAgeCTA4Template,
  videoTemplate,
  getLiveChatCTATemplate,
  getLiveChatCTAV2Template,
  getVueLiveChatCTAV2Template,
  getRingbaPhoneNumberTemplate,
  getHtmlRingbaPhoneNumberTemplate,
  getInnerRingbaPhoneNumberTemplate,
  getHeadlineWithBgTemplate,
  getDynamicDomainNameTemplate,
  getAgeInputQuizTemplate,
  getVueAgeInputQuizTemplate,
  getSpinWheelTemplate,
  getVueSpinWheelTemplate,
  getLiveClaimFeedTemplate,
  getVueLiveClaimFeedTemplate,
  getContactUsFormTemplate,
  getVueContactUsFormTemplate,
  getVueImageBannerTemplate,
  getImageBannerTemplate,
  getSocialProofTemplate,
  getVueSocialProofTemplate,
  getSocialProofV2Template,
  getVueSocialProofV2Template,
  getDynamicButtonsTemplate,
  getNumberedStepsTemplate,
  getVueNumberedStepsTemplate,
  getWarningBannerTemplate,
  getSmallTextBoxTemplate,
  getContentBoxTemplate,
  CONTENT_BOX_FULL_WIDTH_CLASSES,
  getContentBoxFullWidthClass,
  getContentBoxStyle,
  CONTENT_BOX_MOBILE_CLASS,
  CONTENT_BOX_DATA_ATTRIBUTE,
  encodeContentBoxData,
  decodeContentBoxData,
  getContentBoxRowsTemplate,
  getTextUnderlineTemplate,
  getNewsletterBannerTemplate,
  getVueNewsletterBannerTemplate,
};
